import React, { useEffect, useRef, useState } from 'react'
import {
    createDM,
    getCommunityMembers,
    getRocketChatMessages, getRocketChatRooms, getRocketChatThreads, getRocketMessagesReceipt,
    rocketChatLogin
} from '../../api'
import { appStyle } from './chat.sass'
import moment from 'moment'
import { ChannelsWindow } from './chat/channelsSection/channelsWindow'
import { ChatSection } from './chat/chatSection'
import { Community } from './chat/channelsSection/community'
import { loadUser } from '../../utils'

export const Chat = ({ getPerson, setClientPageTitle }) => {
    const [channels, setChannels] = useState([])
    const [selectedChannel, setSelectedChannel] = useState(null)
    const [personId, setPersonId] = useState(null)
    const [messages, setMessages] = useState([])
    const [ws, setWs] = useState(null)
    const [rocketChatData, setRocketChatData] = useState(null)
    const [clientId, setClientId] = useState(null)
    const [readReceipt, setReadReceipt] = useState([])
    const [unredId, setUnredId] = useState(null)
    const [threads, setThreads] = useState([])
    const [showParentThreads, setShowParentThreads] = useState(false)
    const [selectedThread, setSelectedThread] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadingPrev, setLoadingPrev] = useState(false)
    const [loadingNext, setLoadingNext] = useState(false)
    const [client, setClient] = useState(null)
    const [showPinnedMessages, setShowPinnedMessages] = useState(false)
    const [allParentThreads, setAllParentThreads] = useState([])
    const [loadingParentThreads, setLoadingParentThreads] = useState(false)
    const [userAvatars, setUserAvatars] = useState([])
    const [communityMembers, setCommunityMembers] = useState([])
    const [showCommunity, setShowCommunity] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filteredMembers, setFilteredMembers] = useState([])
    const [showSearchSection, setShowSearchSection] = useState(false)
    const [hasMoreNext, setHasMoreNext] = useState(false)
    const [hasMorePrev, setHasMorePrev] = useState(true)
    const [offset, setOffset] = useState(0)
    const [selectedMessageId, setSelectedMessageId] = useState(null)
    const [showChannelMembers, setShowChannelMembers] = useState(false)

    const limit = 100 // or whatever your API supports
    const selectedChannelRef = useRef(null)
    const messagesRef = useRef(messages)
    const currentMessageRef = useRef(null)
    const containerRef = useRef(null)
    const selectedThreadRef = useRef(null)
    const threadsRef = useRef([])
    const firstUnreadRef = useRef(null)
    const readReceiptRef = useRef(null)
    const loginRetried = useRef(false)
    const showParentThreadsRef = useRef(false)
    const scrolledDown = useRef(false)
    const [isSearching, setIsSearching] = useState(false)
    const userRole = loadUser() ? loadUser().role : null

    useEffect(() => {
        const container = containerRef.current
        if (container && offset <= limit ) {
            container.scrollTop = container.scrollHeight
            setTimeout(() => markRoomAsRead(selectedChannel), 3000)
        }
    }, [messages, loading])

    useEffect(() => {
        selectedChannelRef.current = selectedChannel
    }, [selectedChannel])

    useEffect(() => {
        const init = async () => {
            setClientPageTitle && setClientPageTitle(null)
            const rocketChatData = JSON.parse(localStorage.getItem('rocket-chat-data'))
            const person = JSON.parse(localStorage.getItem('phs-user'))
            let client = null
            if (getPerson) client = await getPerson()
            setClient(client)
            if (!rocketChatData && person) {
                const res = await rocketChatLogin(person.id)
                localStorage.setItem('rocket-chat-data', JSON.stringify(res))
                setRocketChatData(res)
            } else setRocketChatData(rocketChatData)
            setPersonId(person.id)
            const rocketChatRoomsParams = {
                person_id: person.id,
                client_id: person.role === 16 ? client.id : person.id
            }
            const res = await getRocketChatRooms(rocketChatRoomsParams)
            if (person.role === 16) {
                setClientId(client.id)
            }
            if (!ws) {
                const webSocket = new WebSocket(process.env.REACT_APP_ROCKET_CHAT_WS_URL)
                setWs(webSocket)
            }
            if (res.rooms && res.rooms.length > 0) {
                const channelsList = res.rooms.filter((room) => room.t === 'p')
                const seen = new Set()
                const dmsList = res.rooms
                    .filter((room) => room.t === 'd')
                    .map((dm) => ({
                        ...dm,
                        name: dm.usernames.filter((username) => username.split('_')[1] !== String(person.id))[0] || '_'
                    }))
                    .filter((dm) => {
                        if (seen.has(dm.name)) return false
                        seen.add(dm.name)
                        return true
                    })
                setChannels([...channelsList, ...dmsList])
                setUserAvatars(res.userAvatars)
                const channelId = channelsList.length > 0 ? channelsList[0]._id: dmsList[0]._id
                setSelectedChannel(channelId)
                onSelectChannel(person.role !== 16 ? person.id : client.id, channelId, res.rooms)
            }
        }
        init()
        return () => {
            if (ws && ws.readyState === WebSocket.OPEN) ws.close()
        }
    }, [])

    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    useEffect(() => {
        if (!selectedChannel) return
        webSocketConnect()
    }, [selectedChannel])

    const webSocketConnect = () => {
        ws.onopen = () => {
            ws.send(JSON.stringify({
                msg: 'connect',
                version: '1',
                support: ['1']
            }))
        }

        ws.onclose = () => {
            setTimeout(webSocketConnect, 1000)
        }

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data)

            if (data.msg === 'connected' || (data.error && !loginRetried)) {
                loginRetried.current = true
                ws.send(JSON.stringify({
                    msg: 'method',
                    method: 'login',
                    id: '1',
                    params: [{
                        resume: rocketChatData.authToken
                    }]
                }))
            }

            if (data.msg === 'ping') {
                ws.send(JSON.stringify({ msg: 'pong' }))
            }

            if (data.msg === 'result' && data.id === '1') {
                loginRetried.current = false

                channels.forEach((channel) => {
                    ws.send(JSON.stringify({
                        msg: 'sub',
                        id: channel._id,
                        name: 'stream-room-messages',
                        params: [
                            channel._id,
                            false
                        ]
                    }))
                })
            }

            if (data.msg === 'changed' && data.collection === 'stream-room-messages' && data.method !== 'sendMessage') {
                const msg = data.fields.args[0]
                msg['ts'] = msg['ts']['$date']
                // if (!msg.replies && msg.u.username.split('_')[1] !== String(personId) && !msg.tmid) {
                //     const newReadReceipt = [...readReceiptRef.current].map((receipt) => {
                //         if (receipt.rid === msg.rid) {
                //             receipt.unread += 1
                //         }
                //         return receipt
                //     })
                // setReadReceipt(newReadReceipt)
                // readReceiptRef.current = newReadReceipt
                // }
                if (msg.u.username.split('_')[1] !== String(personId)) syncReadReceipts()
                if (msg.rid !== selectedChannel || msg.replies || msg.t === 'message_pinned') return
                if (msg.tmid) {
                    if (selectedThreadRef.current && (msg.tmid === selectedThreadRef.current._id)) {
                        const existingThreadIndex = threadsRef.current.findIndex(thread => thread._id === msg._id)
                        if (existingThreadIndex !== -1) {
                            const updatedThreads = [...threadsRef.current]
                            updatedThreads[existingThreadIndex] = msg
                            setThreads(updatedThreads)
                            threadsRef.current = updatedThreads
                            markThreadRead(msg.tmid)
                        } else {
                            const newThreads = [...threadsRef.current, msg]
                            setThreads(newThreads)
                            threadsRef.current = newThreads
                            markThreadRead(msg.tmid)
                        }
                    }
                    syncThreadsCount(msg)
                    if (showParentThreadsRef.current) {
                        fetchAllThreads()
                    }
                    return
                }
                setTimeout(() => markRoomAsRead(selectedChannel), 3000)
                const existingMessageIndex = messagesRef.current.findIndex(message => message._id === msg._id)
                if (existingMessageIndex !== -1) {
                    const updatedMessages = [...messagesRef.current]
                    updatedMessages[existingMessageIndex] = msg
                    setMessages(updatedMessages)
                } else {
                    const newMessages = [...messagesRef.current, msg]
                    setMessages(newMessages)
                }
            }
        }
    }

    const onSelectChannel = async (person_id, id, channelsList = null, isDm = false) => {
        if (loading || id === selectedChannel) return
        setSelectedChannel(id)
        setLoading(true)
        setSelectedThread(null)
        setShowParentThreads(false)
        showParentThreadsRef.current = false
        setShowPinnedMessages(false)
        const res = await getRocketChatMessages({ personId: person_id, roomId: id, isDm, count: limit, next: false, ts: new Date(), inclusive: false, viewer_id: loadUser().id })
        setHasMorePrev(true)
        setUnredId(null)
        firstUnreadRef.current = null
        if (res.success === true) {
            const reversedMessages = res.messages.reverse()
            const channelIds = (channelsList ? channelsList : channels).filter((room) => room.t === 'p' || room.t === 'd').map((channel) => channel._id)
            let receipts = readReceipt
            // if (channelsList) {
            const person = JSON.parse(localStorage.getItem('phs-user'))
            receipts = await getRocketMessagesReceipt({ personId: person.id, channels: channelIds })
            // }
            setReadReceipt(receipts)
            readReceiptRef.current = receipts
            const receipt = receipts.find((receipt) => receipt.rid === id)
            if (receipt && receipt.unread > 0) {
                const firstUnread = reversedMessages.find(msg => moment(msg.ts).isAfter(moment(receipt.ls)))
                if (firstUnread) {
                    setUnredId(firstUnread._id)
                    firstUnreadRef.current = firstUnread._id
                }
            }
            setTimeout(() => markRoomAsRead(id), 3000)
            setMessages(reversedMessages)
        }
        setLoading(false)
        setIsSearching(false)
    }

    const markRoomAsRead = (rid) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
                msg: 'method',
                method: 'readMessages',
                id: 'read',
                params: [rid]
            }))
            syncReadReceipts()
        }
    }

    const syncThreadsCount = (msg, incr = true) => {
        const newMessages = messagesRef.current.map(message => {
            if (message._id === msg.tmid) {
                return {
                    ...message,
                    tcount: (message.tcount || 0) + (incr ? 1 : -1)
                }
            }
            return message
        })
        setMessages(newMessages)
        messagesRef.current = newMessages
    }

    useEffect(() => {
        const handleVisibilityChange = () => {
            console.log('Visibility changed:', document.hidden)
            if (!document.hidden && ws && ws.current && ws.current.readyState !== WebSocket.OPEN) {
                webSocketConnect()
            }
        }

        const handleOnline = () => {
            console.log('Back online, reconnecting WebSocket...')
            if (ws && ws.current && ws.current.readyState !== WebSocket.OPEN) {
                console.log('Back online, reconnected...')
                webSocketConnect()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('online', handleOnline)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('online', handleOnline)
        }
    }, [webSocketConnect, ws])

    const syncReadReceipts = async () => {
        const channelIds = (channels).filter((room) => room.t === 'p' || room.t === 'd').map((channel) => channel._id)
        const person = JSON.parse(localStorage.getItem('phs-user'))
        const receipts = await getRocketMessagesReceipt({ personId: person.id, channels: channelIds })
        setReadReceipt(receipts)
        readReceiptRef.current = receipts
    }

    const markThreadRead = (id) => {
        if (ws) ws.send(JSON.stringify({
            msg: 'method',
            method: 'readThreads',
            id: 'read-thread-root',
            params: [id]
        }))
    }

    const fetchAllThreads = async () => {
        setLoadingParentThreads(true)
        const res = await getRocketChatThreads({
            personId: personId,
            roomId: selectedChannel
        })
        if (res['update']) setAllParentThreads(res.update)
        setLoadingParentThreads(false)
    }

    const fetchCommunityMembers = async () => {
        const res = await getCommunityMembers()
        if (res.success) {
            setCommunityMembers(res.data)
            setFilteredMembers(res.data)
        }
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value)
        const filteredMembers = communityMembers.filter((member) =>
            member.name && member.name.toLowerCase().includes(e.target.value.toLowerCase())
        )
        setFilteredMembers(filteredMembers)
    }

    const handleCreateDM = async (id) => {
        setLoading(true)
        const res = await createDM({
            sender_id: personId,
            recipient_id: id
        })
        if (res.success) {
            setChannels([...channels, {
                ...res.data.room,
                name: res.data.room.usernames.filter((username) => username.split('_')[1] !== String(personId))[0] || '_'
            }])
            setMessages([])
            setSelectedChannel(res.data.room._id)
            setShowCommunity(false)
        }
        setLoading(false)
    }

    const fetchAroundMessages = async(timestamp) => {
        setLoading(true)
        setIsSearching(true)
        const wrapper = document.querySelector('.messageStyle')
        const prevScrollHeight = wrapper ? wrapper.scrollHeight : 0
        const channel = channels.find((ch) => ch._id === selectedChannel)
        const isDm = channel.t === 'd'
        const params = {
            roomId: selectedChannel,
            personId: clientId || personId,
            isDm,
            ts: timestamp,
            count: limit,
            next: false,
            inclusive: true,
            viewer_id: loadUser().id
        }
        const resPrev = await getRocketChatMessages(params)
        const resNext = await getRocketChatMessages({...params, next: true})
        const filteredPrev = resPrev.messages
            .reverse()
        const filteredNext = (isDm ? resNext.messages : resNext.messages.reverse())
        setMessages([...filteredPrev, ...filteredNext.splice(1)])
        const newScrollHeight = wrapper.scrollHeight
        wrapper.scrollTop = Math.max(newScrollHeight - prevScrollHeight, 4)
        setHasMoreNext(true)
        setHasMorePrev(true)
        setLoading(false)
        setIsSearching(false)
    }

    const fetchMessages = async (timestamp, direction, isSearch = false, currentChannel) => {
        console.log(`Fetching messages ${direction} from ${timestamp}...`)
        if (!currentChannel || loading) return

        if (direction === 'up') setLoadingPrev(true)
        else setLoadingNext(true)

        const wrapper = containerRef.current
        const prevScrollHeight = wrapper && wrapper.scrollHeight ? wrapper.scrollHeight : 0
        const prevScrollTop = wrapper && wrapper.scrollTop ? wrapper.scrollTop : 0
        const isDm = currentChannel.t === 'd'

        const res = await getRocketChatMessages({
            roomId: currentChannel._id,
            personId: clientId || personId,
            isDm,
            ts: timestamp,
            count: limit,
            next: direction === 'down',
            inclusive: false,
            viewer_id: loadUser().id
        })

        if (res.messages.length > 0) {
            const filtered = res.messages
                .reverse()

            setMessages(prev => {
                let merged = direction === 'up' ? [...filtered, ...prev] : [...prev, ...filtered]

                const seen = new Set()
                merged = merged.filter(msg => {
                    if (seen.has(msg._id)) return false
                    seen.add(msg._id)
                    return true
                })

                return merged
            })

            if (direction === 'up' && res.messages.length < limit) setHasMorePrev(false)
            if (direction === 'down' && res.messages.length < limit) setHasMoreNext(false)

            setTimeout(() => {
                if (!wrapper) return
                const newScrollHeight = wrapper.scrollHeight
                if (direction === 'up') wrapper.scrollTop = Math.max(newScrollHeight - prevScrollHeight, 0)
                else if (direction === 'down') wrapper.scrollTop = prevScrollTop
            }, 0)
        } else {
            if (direction === 'up') setHasMorePrev(false)
            else setHasMoreNext(false)
        }

        if (direction === 'up') setLoadingPrev(false)
        else setLoadingNext(false)
    }

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let scrollTimeout

        const onScroll = () => {
            if (isSearching) return
            if (scrollTimeout) clearTimeout(scrollTimeout)

            scrollTimeout = setTimeout(() => {
                const scrollTop = container.scrollTop
                const scrollHeight = container.scrollHeight
                const clientHeight = container.clientHeight

                const currentChannel = channels.find(c => c._id === selectedChannel)
                if (!currentChannel) return

                if (scrollTop <= 5 && hasMorePrev && !loadingPrev) {
                    const oldestTs = messagesRef && messagesRef.current[0].ts
                    if (oldestTs) fetchMessages(oldestTs, 'up', false, currentChannel)
                }

                if (scrollHeight - scrollTop - clientHeight <= 10 && hasMoreNext && !loadingNext) {
                    console.log('Fetching more messages down...')
                    const newestTs = messagesRef && messagesRef.current[messagesRef.current.length - 1].ts
                    if (newestTs) fetchMessages(newestTs, 'down', false, currentChannel)
                }
            }, 500)
        }

        container.addEventListener('scroll', onScroll)
        return () => container.removeEventListener('scroll', onScroll)
    }, [hasMoreNext, hasMorePrev, loadingNext, loadingPrev, isSearching, selectedChannel, channels, loading])

    return (
        <>
            <h1 className="title" style={{ paddingTop: 40 }}>
                Chat
            </h1>
            <div
                style={{
                    minHeight: 'calc(100vh - 300px + 90px)'
                }}
            >
                <div className={appStyle}>
                    <div className='container'>
                        <ChannelsWindow
                            channels={channels}
                            readReceipt={readReceipt}
                            selectedChannel={selectedChannel}
                            onSelectChannel={onSelectChannel}
                            client={client}
                            personId={personId}
                            fetchCommunityMembers={fetchCommunityMembers}
                            setShowCommunity={setShowCommunity}
                            setLoading={setLoading}
                            loading={loading}
                        />
                        {showCommunity ? (
                            <Community
                                setShowCommunity={setShowCommunity}
                                searchTerm={searchTerm}
                                handleSearch={handleSearch}
                                filteredMembers={filteredMembers}
                                handleCreateDM={handleCreateDM}
                            />
                        ) : (
                            <ChatSection
                                channels={channels}
                                selectedChannel={selectedChannel}
                                containerRef={containerRef}
                                messages={messages}
                                unredId={unredId}
                                threads={threads}
                                setSelectedThread={setSelectedThread}
                                clientId={clientId}
                                personId={personId}
                                firstUnreadRef={firstUnreadRef}
                                setThreads={setThreads}
                                threadsRef={threadsRef}
                                ws={ws}
                                currentMessageRef={currentMessageRef}
                                selectedThreadRef={selectedThreadRef}
                                loading={loading}
                                loadingNext={loadingNext}
                                loadingPrev={loadingPrev}
                                setShowParentThreads={setShowParentThreads}
                                showParentThreadsRef={showParentThreadsRef}
                                selectedThread={selectedThread}
                                showParentThreads={showParentThreads}
                                receipt={readReceipt.find((receipt) => receipt.rid === selectedChannel) || {}}
                                readReceipt={readReceipt}
                                setReadReceipt={setReadReceipt}
                                setMessages={setMessages}
                                markThreadRead={markThreadRead}
                                showPinnedMessages={showPinnedMessages}
                                setShowPinnedMessages={setShowPinnedMessages}
                                syncThreadsCount={syncThreadsCount}
                                fetchAllThreads={fetchAllThreads}
                                allParentThreads={allParentThreads}
                                loadingParentThreads={loadingParentThreads}
                                userAvatars={userAvatars}
                                showCommunity={showCommunity}
                                showSearchSection={showSearchSection}
                                setShowSearchSection={setShowSearchSection}
                                selectedMessageId={selectedMessageId}
                                setSelectedMessageId={setSelectedMessageId}
                                fetchAroundMessages={fetchAroundMessages}
                                userRole={userRole}
                                setShowChannelMembers={setShowChannelMembers}
                                showChannelMembers={showChannelMembers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
